/**
 * Schedule mode helpers (SCHEDULER.md §11.1a) — the unified editor's modes.
 *
 *   TIME         — clock-driven (existing cadence presets).
 *   ONCE         — run the prompt list once.
 *   REPEAT_N     — run the whole list N times.
 *   REPEAT_UNTIL — loop until the result gate (objective + rating + confidence).
 *
 * Pure: build the API body, validate, humanize for the card.
 */

export const SCHEDULE_MODES = ['TIME', 'ONCE', 'REPEAT_N', 'REPEAT_UNTIL'];
export const MODE_LABEL = {
  TIME: 'Time',
  ONCE: 'Once',
  REPEAT_N: 'Repeat N',
  REPEAT_UNTIL: 'Repeat until',
};
export const LOOP_MODES = ['ONCE', 'REPEAT_N', 'REPEAT_UNTIL'];

export const DEFAULT_SEPARATOR = '\n\n';
export const DEFAULT_MAX_TURNS = 10;

export function isLoopMode(mode) {
  return LOOP_MODES.includes(String(mode || '').toUpperCase());
}

/** Split an uploaded blob into prompts on the user's separator. */
export function splitPrompts(text, separator = DEFAULT_SEPARATOR) {
  const sep = separator || DEFAULT_SEPARATOR;
  return String(text || '')
    .split(sep)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Build the loop create/update body from the editor state.
 * @param {object} s { mode, prompts:[], separator, repeatN, objective, rating, confidence, maxTurns, deadline }
 */
export function buildLoopBody(s = {}) {
  const mode = String(s.mode || '').toUpperCase();
  const body = {
    mode,
    prompts: (s.prompts || []).map((p) => String(p).trim()).filter(Boolean),
    separator: s.separator || DEFAULT_SEPARATOR,
    maxTurns: Number(s.maxTurns) || DEFAULT_MAX_TURNS,
  };
  if (s.deadline) body.deadline = s.deadline;
  if (mode === 'REPEAT_N') body.repeatN = Number(s.repeatN) || 1;
  if (mode === 'REPEAT_UNTIL') {
    body.gate = {
      objective: String(s.objective || '').trim(),
      rating: Number(s.rating),
      confidence: Number(s.confidence),
    };
  }
  return body;
}

/** Returns '' if valid, else a user-facing error string. */
export function validateLoop(s = {}) {
  const mode = String(s.mode || '').toUpperCase();
  const prompts = (s.prompts || []).map((p) => String(p).trim()).filter(Boolean);
  if (!prompts.length) return 'Add at least one prompt.';
  if ((Number(s.maxTurns) || DEFAULT_MAX_TURNS) < 1) return 'Max turns must be at least 1.';
  if (mode === 'REPEAT_N' && (!s.repeatN || Number(s.repeatN) < 1)) {
    return 'Set how many times to repeat (≥ 1).';
  }
  if (mode === 'REPEAT_UNTIL') {
    if (!String(s.objective || '').trim()) return 'Describe the objective to grade against.';
    const r = Number(s.rating);
    const c = Number(s.confidence);
    if (!(r > 0 && r < 10)) return 'Target rating must be between 0 and 10 (10 not allowed).';
    if (!(c > 0 && c <= 1)) return 'Target confidence must be between 0 and 1.';
  }
  return '';
}

/** Rating dropdown options: 0.5 … 9.5 (10 is excluded — can't demand perfection). */
export function ratingOptions() {
  const out = [];
  for (let i = 1; i < 20; i += 1) out.push((i * 0.5).toFixed(1)); // 0.5..9.5
  return out;
}

/** Card headline for a loop schedule. */
export function humanizeMode(schedule) {
  const mode = String(schedule?.mode || 'TIME').toUpperCase();
  if (mode === 'ONCE') return 'Run once';
  if (mode === 'REPEAT_N') return `Repeat ${schedule?.repeatN ?? '?'}×`;
  if (mode === 'REPEAT_UNTIL') {
    const g = schedule?.gate || {};
    return `Until ${g.rating ?? '?'}/10 @ ${g.confidence ?? '?'}`;
  }
  return 'Time';
}
